import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { 
  HiOutlineIdentification, 
  HiOutlinePhone, 
  HiOutlineMail, 
  HiOutlineOfficeBuilding,
  HiOutlineLocationMarker,
  HiOutlineCalendar,
  HiOutlineUserCircle,
  HiOutlineGlobe,
  HiOutlineHeart,
  HiOutlineClipboardCheck,
  HiOutlineDocumentDuplicate,
  HiOutlineDocumentText,
  HiOutlineCreditCard,
  HiOutlineCash,
  HiOutlineDocument,
  HiOutlinePencil,
  HiOutlineLink,
  HiOutlineAcademicCap,
  HiOutlineDocumentDownload,
  HiOutlineCheckCircle
} from 'react-icons/hi';
import { 
  HiOutlineLanguage
} from 'react-icons/hi2';
import { 
  FaLinkedin, 
  FaTwitter, 
  FaFacebook, 
  FaInstagram
} from 'react-icons/fa';
import { fetchUserPayslips } from '../../store/slices/payslipSlice';
import { fetchBankDetails } from '../../store/slices/bankSlice';
import { fetchAdditionalDetails } from '../../store/slices/additionalDetailsSlice';
import BankDetailsModal from '../../components/BankDetailsModal';
import DeclarationModal from '../../components/DeclarationModal';
import ProfilePictureModal from '../../components/ProfilePictureModal';
import SocialHandlesModal from '../../components/SocialHandlesModal';
import { 
  PersonalInfoForm, 
  EmergencyContactForm, 
  DocumentsUploadForm, 
  BiographyForm 
} from '../../components/ProfileEditForms';
import './Profile.scss';

// Helper function to format date from timestamp
const formatDate = (timestamp) => {
  if (!timestamp) return null;
  
  try {
    // Check if timestamp is a Firebase timestamp (has seconds and nanoseconds)
    if (timestamp && typeof timestamp === 'object' && timestamp.seconds) {
      // Convert Firebase timestamp to JavaScript Date
      return new Date(timestamp.seconds * 1000).toLocaleDateString('en-US', {
        year: 'numeric', 
        month: 'long', 
        day: 'numeric'
      });
    }
    
    // Handle regular Date objects or date strings
    const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric', 
      month: 'long', 
      day: 'numeric'
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid Date';
  }
};

// Helper function to get time-based greeting
const getGreeting = () => {
  const hours = new Date().getHours();
  if (hours < 12) return 'Good Morning';
  if (hours < 18) return 'Good Afternoon';
  return 'Good Evening';
};

// Update getDaysWithCompany function to handle Firebase timestamps
const getDaysWithCompany = (joinDate) => {
  if (!joinDate) return null;
  
  try {
    let joinDateTime;
    
    // Check if it's a Firebase timestamp
    if (joinDate && typeof joinDate === 'object' && joinDate.seconds) {
      joinDateTime = new Date(joinDate.seconds * 1000);
    } else {
      joinDateTime = joinDate instanceof Date ? joinDate : new Date(joinDate);
    }
    
    const today = new Date();
    
    // Calculate difference in milliseconds
    const diffTime = Math.abs(today - joinDateTime);
    // Convert to days
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  } catch (error) {
    console.error('Error calculating days with company:', error);
    return null;
  }
};

// Function to calculate years with company based on DOJ
const getYearsWithCompany = (joinDate) => {
  if (!joinDate) return 0;
  
  try {
    let joinDateTime;
    
    // Check if it's a Firebase timestamp
    if (joinDate && typeof joinDate === 'object' && joinDate.seconds) {
      joinDateTime = new Date(joinDate.seconds * 1000);
    } else {
      joinDateTime = joinDate instanceof Date ? joinDate : new Date(joinDate);
    }
    
    const today = new Date();
    
    // Calculate the difference in years
    let years = today.getFullYear() - joinDateTime.getFullYear();
    
    // Adjust if the anniversary hasn't occurred this year
    const monthDiff = today.getMonth() - joinDateTime.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < joinDateTime.getDate())) {
      years--;
    }
    
    // Return with one decimal place for more accuracy
    const months = today.getMonth() - joinDateTime.getMonth() + (years * 12);
    const totalMonths = months + (today.getDate() >= joinDateTime.getDate() ? 0 : -1);
    const yearsWithDecimal = Math.max(0, totalMonths / 12);
    
    return parseFloat(yearsWithDecimal.toFixed(1));
  } catch (error) {
    console.error('Error calculating years with company:', error);
    return 0;
  }
};

const Profile = () => {
  const dispatch = useDispatch();
  const { email: emailParam } = useParams(); // Get email from URL params if available
  const { user, employees } = useSelector(state => state.auth);
  const { items: payslips, loading: payslipsLoading } = useSelector(state => state.payslips);
  const { details: bankDetails, loading: bankLoading } = useSelector(state => state.bank);
  const { details: declarationDetails, loading: declarationLoading } = useSelector(state => state.additionalDetails);
  const [loading, setLoading] = useState(true);
  const [profileData, setProfileData] = useState({});
  const [activeTab, setActiveTab] = useState(() => {
    // Check if there's a stored active tab in session storage
    const storedTab = sessionStorage.getItem('profileActiveTab');
    if (storedTab) {
      // Clear the session storage so it doesn't persist across page refreshes
      sessionStorage.removeItem('profileActiveTab');
      return storedTab;
    }
    return 'personal';
  });
  const [isBankModalOpen, setIsBankModalOpen] = useState(false);
  const [isDeclarationModalOpen, setIsDeclarationModalOpen] = useState(false);
  
  // New state for edit modals
  const [isPersonalInfoModalOpen, setIsPersonalInfoModalOpen] = useState(false);
  const [isEmergencyContactModalOpen, setIsEmergencyContactModalOpen] = useState(false);
  const [isDocumentsModalOpen, setIsDocumentsModalOpen] = useState(false);
  const [isBiographyModalOpen, setIsBiographyModalOpen] = useState(false);
  const [isProfilePictureModalOpen, setIsProfilePictureModalOpen] = useState(false);
  const [isSocialHandlesModalOpen, setIsSocialHandlesModalOpen] = useState(false);
  const [isUpdatingProfilePicture, setIsUpdatingProfilePicture] = useState(false);
  const [isUpdatingSocialHandles, setIsUpdatingSocialHandles] = useState(false);
  
  // Find the current user's data or the user specified in the URL
  useEffect(() => {
    if (employees.length > 0) {
      // If email param exists, fetch that user's profile, otherwise show current user
      const targetEmail = emailParam || (user && user.email);
      
      if (targetEmail) {
        const userData = employees.find(employee => employee.email === targetEmail);
        if (userData) {
          setProfileData(userData);
          setLoading(false);
        }
      }
    }
  }, [user, employees, emailParam]);
  
  // Fetch user data
  useEffect(() => {
    // Get the target email - either from URL or current user
    const targetEmail = emailParam || (user && user.email);
    
    if (targetEmail) {
      dispatch(fetchUserPayslips(targetEmail));
      dispatch(fetchBankDetails(targetEmail));
      dispatch(fetchAdditionalDetails(targetEmail));
    }
  }, [dispatch, user, emailParam]);
  
  if (loading) {
    return (
      <div className="profile-loading">
        <div className="loading-spinner"></div>
        <p>Loading profile information...</p>
      </div>
    );
  }
  
  return (
    <div className="profile-container">
      <div className="profile-cover">
        <div className="cover-gradient">
          <div className="cover-dots"></div>
          <div className="cover-content">
            <div className="profile-avatar-wrapper">
              <div className="profile-avatar">
                {isUpdatingProfilePicture ? (
                  <div className="avatar-loading">
                    <div className="loading-spinner"></div>
                  </div>
                ) : (
                  <>
                    {profileData.img ? (
                      <img src={profileData.img} alt={profileData.name} />
                    ) : (
                      <div className="profile-avatar-placeholder">
                        {profileData.name && profileData.name.charAt(0)}
                      </div>
                    )}
                    <div className="avatar-status">
                      <span className={`status-indicator ${profileData.myelinLive ? 'active' : 'inactive'}`}></span>
                    </div>
                    <button 
                      className="avatar-edit-button"
                      onClick={() => setIsProfilePictureModalOpen(true)}
                      title="Edit profile picture"
                      disabled={isUpdatingProfilePicture}
                    >
                      ✏️
                    </button>
                  </>
                )}
              </div>
            </div>
            
            <div className="profile-intro">
              <div className="greeting-message">
                <span>
                  {getGreeting()}, {profileData.name.split(' ')[0]}!
                  {profileData.doj && getDaysWithCompany(profileData.doj) && (
                    <span className="days-info">
                      It's your {getDaysWithCompany(profileData.doj)}th day with MyelinZ
                    </span>
                  )}
                </span>
              </div>
              <h1 className="profile-name">{profileData.name}</h1>
              <div className="profile-title-wrapper">
                <p className="profile-title">{profileData.designation}</p>
                <div className="profile-badge">
                  <HiOutlineCheckCircle />
                  <span>Verified</span>
                </div>
              </div>
              <div className="profile-meta-details">
                <div className="meta-item">
                  <HiOutlineLocationMarker className="meta-icon" />
                  <span>{profileData.location || 'Location not specified'}</span>
                </div>
                <div className="meta-item">
                  <HiOutlineIdentification className="meta-icon" />
                  <span>ID: {profileData.empID}</span>
                </div>
                <div className="meta-item">
                  <HiOutlineCalendar className="meta-icon" />
                  <span>Joined: {formatDate(profileData.doj)}</span>
                </div>
              </div>
              
              <div className="profile-social">
                {isUpdatingSocialHandles ? (
                  <div className="social-loading">
                    <div className="loading-spinner"></div>
                    <span>Updating social handles...</span>
                  </div>
                ) : (
                  <>
                    <div className="social-links">
                      {profileData.socialLinkedIn && (
                        <a 
                          href={profileData.socialLinkedIn} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="social-link linkedin"
                          title="LinkedIn Profile"
                        >
                          <FaLinkedin />
                        </a>
                      )}
                      {profileData.socialTwitter && (
                        <a 
                          href={profileData.socialTwitter} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="social-link twitter"
                          title="Twitter Profile"
                        >
                          <FaTwitter />
                        </a>
                      )}
                      {profileData.socialFacebook && (
                        <a 
                          href={profileData.socialFacebook} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="social-link facebook"
                          title="Facebook Profile"
                        >
                          <FaFacebook />
                        </a>
                      )}
                      {profileData.socialInstagram && (
                        <a 
                          href={profileData.socialInstagram} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="social-link instagram"
                          title="Instagram Profile"
                        >
                          <FaInstagram />
                        </a>
                      )}
                      {profileData.socialWeb && (
                        <a 
                          href={profileData.socialWeb} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="social-link website"
                          title="Personal Website"
                        >
                          <HiOutlineGlobe />
                        </a>
                      )}
                    </div>
                    <button 
                      className="social-edit-button"
                      onClick={() => setIsSocialHandlesModalOpen(true)}
                      title="Edit social handles"
                      disabled={isUpdatingSocialHandles}
                    >
                      ✏️
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="profile-stats-wrapper">
        <div className="stats-container">
          <div className="stat-card">
            <div className="stat-icon leave-taken">
              <HiOutlineClipboardCheck />
            </div>
            <div className="stat-info">
              <div className="stat-value">{profileData.leaveTaken || 0}</div>
              <div className="stat-label">Leaves Taken</div>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon leave-balance">
              <HiOutlineCalendar />
            </div>
            <div className="stat-info">
              <div className="stat-value">{profileData.leaveBalance || 0}</div>
              <div className="stat-label">Leave Balance</div>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon status">
              <HiOutlineCheckCircle />
            </div>
            <div className="stat-info">
              <div className="stat-value">Active</div>
              <div className="stat-label">Status</div>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon experience">
              <HiOutlineAcademicCap />
            </div>
            <div className="stat-info">
              <div className="stat-value">{getYearsWithCompany(profileData.doj)} years</div>
              <div className="stat-label">Years with MyelinZ</div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="profile-tabs-container">
        <div className="profile-tabs">
          <div 
            className={`tab-item ${activeTab === 'personal' ? 'active' : ''}`} 
            onClick={() => setActiveTab('personal')}
          >
            <HiOutlineUserCircle className="tab-icon" />
            <span>Personal</span>
          </div>
          <div 
            className={`tab-item ${activeTab === 'emergency' ? 'active' : ''}`} 
            onClick={() => setActiveTab('emergency')}
          >
            <HiOutlineHeart className="tab-icon" />
            <span>Emergency</span>
          </div>
          <div 
            className={`tab-item ${activeTab === 'documents' ? 'active' : ''}`} 
            onClick={() => setActiveTab('documents')}
          >
            <HiOutlineDocumentDuplicate className="tab-icon" />
            <span>Documents</span>
          </div>
          <div 
            className={`tab-item ${activeTab === 'bank' ? 'active' : ''}`} 
            onClick={() => setActiveTab('bank')}
          >
            <HiOutlineCreditCard className="tab-icon" />
            <span>Bank</span>
          </div>
          <div 
            className={`tab-item ${activeTab === 'declaration' ? 'active' : ''}`} 
            onClick={() => setActiveTab('declaration')}
          >
            <HiOutlineDocument className="tab-icon" />
            <span>Declaration</span>
          </div>
          <div 
            className={`tab-item ${activeTab === 'payslips' ? 'active' : ''}`} 
            onClick={() => setActiveTab('payslips')}
          >
            <HiOutlineCash className="tab-icon" />
            <span>Payslips</span>
          </div>
          <div 
            className={`tab-item ${activeTab === 'bio' ? 'active' : ''}`} 
            onClick={() => setActiveTab('bio')}
          >
            <HiOutlineLink className="tab-icon" />
            <span>Biography</span>
          </div>
        </div>
        
        <div className="profile-content-wrapper">
          <div className="tab-content-container">
            {activeTab === 'personal' && (
              <div className="tab-content personal-info-content">
                <h2 className="section-title">
                  <HiOutlineUserCircle className="section-icon" />
                  Personal Information
                  <span className="edit-button" onClick={() => setIsPersonalInfoModalOpen(true)}>
                    <HiOutlinePencil /> Edit
                  </span>
                </h2>
                <div className="info-grid">
                  <div className="info-card">
                    <div className="info-label">
                      <HiOutlineIdentification className="info-icon" />
                      Employee ID
                    </div>
                    <div className="info-value">{profileData.empID || 'N/A'}</div>
                  </div>
                  
                  <div className="info-card">
                    <div className="info-label">
                      <HiOutlinePhone className="info-icon" />
                      Phone
                    </div>
                    <div className="info-value">{profileData.phone || 'N/A'}</div>
                  </div>
                  
                  <div className="info-card">
                    <div className="info-label">
                      <HiOutlineMail className="info-icon" />
                      Email
                    </div>
                    <div className="info-value">{profileData.email || 'N/A'}</div>
                  </div>
                  
                  <div className="info-card">
                    <div className="info-label">
                      <HiOutlineCalendar className="info-icon" />
                      Date of Birth
                    </div>
                    <div className="info-value">{formatDate(profileData.dob) || 'N/A'}</div>
                  </div>
                  
                  <div className="info-card">
                    <div className="info-label">
                      <HiOutlineOfficeBuilding className="info-icon" />
                      Department
                    </div>
                    <div className="info-value">{profileData.department || 'N/A'}</div>
                  </div>
                  
                  <div className="info-card">
                    <div className="info-label">
                      <HiOutlineLocationMarker className="info-icon" />
                      Location
                    </div>
                    <div className="info-value">{profileData.location || 'N/A'}</div>
                  </div>
                  
                  <div className="info-card">
                    <div className="info-label">
                      <HiOutlineCalendar className="info-icon" />
                      Date Joined
                    </div>
                    <div className="info-value">
                      {formatDate(profileData.doj) || 'N/A'}
                      {profileData.doj && getDaysWithCompany(profileData.doj) && (
                        <div className="days-with-company">
                          <span>{getDaysWithCompany(profileData.doj)} days with MyelinZ</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="info-card">
                    <div className="info-label">
                      <HiOutlineLanguage className="info-icon" />
                      Languages
                    </div>
                    <div className="info-value">{profileData.languages || 'N/A'}</div>
                  </div>
                </div>
              </div>
            )}
            
            {activeTab === 'emergency' && (
              <div className="tab-content emergency-contact-content">
                <h2 className="section-title">
                  <HiOutlineHeart className="section-icon" />
                  Emergency Contact
                  <span className="edit-button" onClick={() => setIsEmergencyContactModalOpen(true)}>
                    <HiOutlinePencil /> Edit
                  </span>
                </h2>
                <div className="emergency-grid">
                  <div className="contact-card">
                    <div className="contact-header">
                      <h3>Primary Contact</h3>
                    </div>
                    <div className="contact-details">
                      <p className="contact-name">{profileData.emergencyContact || 'Not specified'}</p>
                      <p className="contact-relation">
                        <span className="label">Relation:</span> {profileData.emergencyRelation || 'Not specified'}
                      </p>
                      <p className="contact-phone">
                        <span className="label">Phone:</span> {profileData.emergencyNumber || 'Not specified'}
                      </p>
                      <p className="contact-address">
                        <span className="label">Address:</span> {profileData.emergencyContactAddress || 'Not specified'}
                      </p>
                    </div>
                  </div>
                  
                  <div className="contact-card">
                    <div className="contact-header">
                      <h3>Secondary Contact</h3>
                    </div>
                    <div className="contact-details">
                      <p className="contact-name">{profileData.secondaryEmergencyContact || 'Not specified'}</p>
                      <p className="contact-relation">
                        <span className="label">Relation:</span> {profileData.secondaryEmergencyRelation || 'Not specified'}
                      </p>
                      <p className="contact-phone">
                        <span className="label">Phone:</span> {profileData.secondaryEmergencyNumber || 'Not specified'}
                      </p>
                      <p className="contact-address">
                        <span className="label">Address:</span> {profileData.secondaryEmergencyAddress || 'Not specified'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {activeTab === 'documents' && (
              <div className="tab-content documents-content">
                <h2 className="section-title">
                  <HiOutlineDocumentDuplicate className="section-icon" />
                  Documents
                  <span className="edit-button" onClick={() => setIsDocumentsModalOpen(true)}>
                    <HiOutlinePencil /> Upload
                  </span>
                </h2>
                <div className="documents-grid">
                  {[
                    { 
                      title: 'Resume/CV',
                      icon: <HiOutlineDocumentText />,
                      url: profileData.resumeUrl || null,
                    },
                    { 
                      title: 'ID Proof',
                      icon: <HiOutlineIdentification />,
                      url: profileData.idProofUrl || null,
                    },
                    { 
                      title: 'Experience Certificate',
                      icon: <HiOutlineClipboardCheck />,
                      url: profileData.expCertificateUrl || null,
                    },
                    { 
                      title: 'Education Certificate',
                      icon: <HiOutlineAcademicCap />,
                      url: profileData.eduCertificateUrl || null,
                    }
                  ].map((doc, index) => (
                    <div className="document-card" key={index}>
                      <div className="document-icon">
                        {doc.icon}
                      </div>
                      <div className="document-info">
                        <h3 className="document-title">{doc.title}</h3>
                        <div className="document-actions">
                          {doc.url ? (
                            <>
                              <a href={doc.url} className="doc-action-btn view" target="_blank" rel="noopener noreferrer">
                                View
                              </a>
                              <a href={doc.url} className="doc-action-btn download" download>
                                Download
                              </a>
                            </>
                          ) : (
                            <span className="doc-not-available">Not uploaded</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {activeTab === 'bank' && (
              <div className="tab-content bank-content">
                <h2 className="section-title">
                  <HiOutlineCreditCard className="section-icon" />
                  Bank Details
                  <span className="edit-button" onClick={() => setIsBankModalOpen(true)}>
                    <HiOutlinePencil /> Edit
                  </span>
                </h2>
                
                {bankLoading ? (
                  <div className="section-loading">
                    <div className="loading-spinner"></div>
                    <p>Loading bank details...</p>
                  </div>
                ) : !bankDetails ? (
                  <div className="section-empty">
                    <p>No bank details available.</p>
                    <button className="action-button" onClick={() => setIsBankModalOpen(true)}>
                      Add Bank Details
                    </button>
                  </div>
                ) : (
                  <div className="bank-details-wrapper">
                    <div className="bank-card">
                      <div className="card-chip"></div>
                      <div className="card-logo"></div>
                      <div className="card-number">
                        {bankDetails.accountNumber || 'No Account Number'}
                      </div>
                      <div className="card-info">
                        <div className="card-name">
                          <span className="label">ACCOUNT HOLDER</span>
                          <span className="value">{bankDetails.accountName || 'Not Specified'}</span>
                        </div>
                        <div className="card-expires">
                          <span className="label">BANK</span>
                          <span className="value">{bankDetails.bankName || 'Not Specified'}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="details-grid">
                      <div className="detail-card">
                        <span className="detail-label">Account Number</span>
                        <span className="detail-value">{bankDetails.accountNumber || 'Not Specified'}</span>
                      </div>
                      <div className="detail-card">
                        <span className="detail-label">Bank Type</span>
                        <span className="detail-value">
                          {(bankDetails.bankType || (bankDetails.iban ? 'international' : 'uk')) === 'international'
                            ? 'International'
                            : 'UK-based'}
                        </span>
                      </div>
                      <div className="detail-card">
                        <span className="detail-label">Sort Code</span>
                        <span className="detail-value">
                          {(bankDetails.bankType || (bankDetails.iban ? 'international' : 'uk')) === 'uk'
                            ? bankDetails.sortCode || 'Not Specified'
                            : 'Not Applicable'}
                        </span>
                      </div>
                      <div className="detail-card">
                        <span className="detail-label">IBAN</span>
                        <span className="detail-value">
                          {(bankDetails.bankType || (bankDetails.iban ? 'international' : 'uk')) === 'international'
                            ? bankDetails.iban || 'Not Specified'
                            : 'Not Applicable'}
                        </span>
                      </div>
                      <div className="detail-card">
                        <span className="detail-label">Branch</span>
                        <span className="detail-value">{bankDetails.branchName || bankDetails.branch || 'Not Specified'}</span>
                      </div>
                      <div className="detail-card">
                        <span className="detail-label">SWIFT Code</span>
                        <span className="detail-value">{bankDetails.swiftCode || 'Not Specified'}</span>
                      </div>
                      <div className="detail-card">
                        <span className="detail-label">Routing Number</span>
                        <span className="detail-value">{bankDetails.routingNumber || 'Not Specified'}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {activeTab === 'declaration' && (
              <div className="tab-content declaration-content">
                <h2 className="section-title">
                  <HiOutlineDocument className="section-icon" />
                  Declaration
                  <span className="edit-button" onClick={() => setIsDeclarationModalOpen(true)}>
                    <HiOutlinePencil /> Edit
                  </span>
                </h2>
                
                {declarationLoading ? (
                  <div className="section-loading">
                    <div className="loading-spinner"></div>
                    <p>Loading declaration details...</p>
                  </div>
                ) : !declarationDetails ? (
                  <div className="section-empty">
                    <p>No declaration information available.</p>
                    <button className="action-button" onClick={() => setIsDeclarationModalOpen(true)}>
                      Add Declaration
                    </button>
                  </div>
                ) : (
                  <div className="declaration-wrapper">
                    <div className="declaration-section">
                      <h3 className="declaration-heading">Educational Qualifications</h3>
                      <div className="qualification-box">
                        {declarationDetails.declarationHighestQualification || 'No information provided'}
                      </div>
                    </div>
                    
                    <div className="declaration-section">
                      <h3 className="declaration-heading">Criminal Convictions</h3>
                      <div className="medical-declaration">
                        <p className="declaration-question">
                          Do you have any unspent criminal convictions?
                        </p>
                        <div className={`declaration-answer ${declarationDetails.declarationConvictionStatus === 'YES' ? 'yes' : 'no'}`}>
                          {declarationDetails.declarationConvictionStatus === 'YES' ? 'Yes' : 'No'}
                        </div>
                        {declarationDetails.declarationConvictionStatus === 'YES' && declarationDetails.declarationConvictionDetails && (
                          <div className="declaration-details">
                            {declarationDetails.declarationConvictionDetails}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="declaration-section">
                      <h3 className="declaration-heading">Disability Status</h3>
                      <div className="medical-declaration">
                        <p className="declaration-question">
                          Do you have a disability or health condition that we should be aware of?
                        </p>
                        <div className={`declaration-answer ${declarationDetails.declarationDisabilityStatus === 'YES' ? 'yes' : 'no'}`}>
                          {declarationDetails.declarationDisabilityStatus === 'YES' ? 'Yes' : 'No'}
                        </div>
                        {declarationDetails.declarationDisabilityStatus === 'YES' && declarationDetails.declarationDisabilityDetails && (
                          <div className="declaration-details">
                            {declarationDetails.declarationDisabilityDetails}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="declaration-footer">
                      <p className="updated-at">
                        Last updated: {declarationDetails.lastUpdated ? formatDate(declarationDetails.lastUpdated) : 'N/A'}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {activeTab === 'payslips' && (
              <div className="tab-content payslips-content">
                <h2 className="section-title">
                  <HiOutlineCash className="section-icon" />
                  Payslips
                </h2>
                
                {payslipsLoading ? (
                  <div className="section-loading">
                    <div className="loading-spinner"></div>
                    <p>Loading payslips...</p>
                  </div>
                ) : payslips.length === 0 ? (
                  <div className="section-empty">
                    <p>No payslips available yet.</p>
                  </div>
                ) : (
                  <div className="payslips-wrapper">
                    <div className="payslips-grid">
                      {payslips.map((payslip, index) => {
                        // Format the date - handle Firebase timestamp or older format
                        let month, year;
                        
                        if (payslip.payMonth) {
                          // New format: "YYYY-MMM"
                          const parts = payslip.payMonth.split('-');
                          if (parts.length === 2) {
                            year = parts[0];
                            month = parts[1];
                          }
                        } else if (payslip.payPeriod) {
                          // Legacy format support
                          const payPeriodDate = payslip.payPeriod.toDate ? 
                            payslip.payPeriod.toDate() : 
                            (payslip.payPeriod instanceof Date ? payslip.payPeriod : new Date());
                          
                          month = payPeriodDate.toLocaleString('default', { month: 'short' }).toUpperCase();
                          year = payPeriodDate.getFullYear();
                        } else {
                          // Fallback
                          month = "N/A";
                          year = "N/A";
                        }
                        
                        // Format creation date
                        const createdDate = payslip.createdAt?.toDate ? 
                          formatDate(payslip.createdAt.toDate()) : 
                          (payslip.createdAt ? formatDate(new Date(payslip.createdAt)) : 'N/A');
                        
                        return (
                          <div className="payslip-card" key={payslip.id || index}>
                            <div className="payslip-header">
                              <div className="month-year">
                                {month} {year}
                              </div>
                              <div className="created-date">
                                {createdDate}
                              </div>
                            </div>
                            <div className="payslip-body">
                              <div className="payslip-icon">
                                <HiOutlineDocumentText />
                              </div>
                              <div className="payslip-details">
                                <h3 className="payslip-title">Salary Slip</h3>
                                <p className="payslip-description">
                                  Your salary statement for {month} {year}
                                </p>
                              </div>
                            </div>
                            <div className="payslip-footer">
                              <a 
                                href={payslip.url || "#"} 
                                className="download-button"
                                target="_blank" 
                                rel="noopener noreferrer"
                                onClick={(e) => {
                                  if (!payslip.url) {
                                    e.preventDefault();
                                    alert('Download link not available.');
                                  }
                                }}
                              >
                                <HiOutlineDocumentDownload />
                                <span>Download</span>
                              </a>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {activeTab === 'bio' && (
              <div className="tab-content bio-content">
                <h2 className="section-title">
                  <HiOutlineLink className="section-icon" />
                  Biography
                  <span className="edit-button" onClick={() => setIsBiographyModalOpen(true)}>
                    <HiOutlinePencil /> Edit
                  </span>
                </h2>
                
                <div className="bio-wrapper">
                  {profileData.bio ? (
                    <div className="bio-text">
                      {profileData.bio.split('\n').map((paragraph, idx) => (
                        <p key={idx}>{paragraph}</p>
                      ))}
                    </div>
                  ) : (
                    <div className="section-empty">
                      <p>No biography information available.</p>
                      <button 
                        className="action-button"
                        onClick={() => setIsBiographyModalOpen(true)}
                      >
                        Add Biography
                      </button>
                    </div>
                  )}
                  
                  <div className="bio-section">
                    <h3 className="bio-section-title">Skills</h3>
                    <div className="skills-container">
                      {profileData.skills ? (
                        profileData.skills.split(',').map((skill, index) => (
                          <span className="skill-tag" key={index}>{skill.trim()}</span>
                        ))
                      ) : (
                        <p className="no-data">No skills listed</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="bio-section">
                    <h3 className="bio-section-title">Interests</h3>
                    <div className="interests-container">
                      {profileData.interests ? (
                        profileData.interests.split(',').map((interest, index) => (
                          <span className="interest-tag" key={index}>{interest.trim()}</span>
                        ))
                      ) : (
                        <p className="no-data">No interests listed</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {isBankModalOpen && (
        <BankDetailsModal 
          isOpen={isBankModalOpen}
          onClose={() => setIsBankModalOpen(false)}
          bankDetails={bankDetails}
          userEmail={user.email}
        />
      )}
      
      {isDeclarationModalOpen && (
        <DeclarationModal
          isOpen={isDeclarationModalOpen}
          onClose={() => setIsDeclarationModalOpen(false)}
          declarationDetails={declarationDetails}
          userEmail={user.email}
        />
      )}
      
      {/* New edit modals */}
      {isPersonalInfoModalOpen && (
        <PersonalInfoForm 
          isOpen={isPersonalInfoModalOpen}
          onClose={() => setIsPersonalInfoModalOpen(false)}
          userData={profileData}
        />
      )}
      
      {isEmergencyContactModalOpen && (
        <EmergencyContactForm
          isOpen={isEmergencyContactModalOpen}
          onClose={() => setIsEmergencyContactModalOpen(false)}
          userData={profileData}
        />
      )}
      
      {isDocumentsModalOpen && (
        <DocumentsUploadForm
          isOpen={isDocumentsModalOpen}
          onClose={() => setIsDocumentsModalOpen(false)}
          userData={profileData}
        />
      )}
      
      {isBiographyModalOpen && (
        <BiographyForm
          isOpen={isBiographyModalOpen}
          onClose={() => setIsBiographyModalOpen(false)}
          userData={profileData}
        />
      )}
      
      {isProfilePictureModalOpen && (
        <ProfilePictureModal
          isOpen={isProfilePictureModalOpen}
          onClose={() => setIsProfilePictureModalOpen(false)}
          userData={profileData}
          onUpdateStart={() => setIsUpdatingProfilePicture(true)}
          onUpdateComplete={() => setIsUpdatingProfilePicture(false)}
        />
      )}
      
      {isSocialHandlesModalOpen && (
        <SocialHandlesModal
          isOpen={isSocialHandlesModalOpen}
          onClose={() => setIsSocialHandlesModalOpen(false)}
          userData={profileData}
          onUpdateStart={() => setIsUpdatingSocialHandles(true)}
          onUpdateComplete={() => setIsUpdatingSocialHandles(false)}
        />
      )}
    </div>
  );
};

export default Profile; 